import React, { useEffect, useState } from 'react'
import { MetaTitle, LoadError } from '../../Components/UI'
import Grid from '@material-ui/core/Grid'
import { styled } from '@mui/material'
import { createTheme, ThemeProvider } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'
import { ProposalInfoCard } from './ProposalInfoCard'
import { FeatureBlock } from './FeatureBlocks'
import { s } from './style'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { StaticContext } from 'react-router'
import { isEqual } from 'lodash'
import { ROUTES } from '../../Constants'
import {
  useProposalGetProposalQueryQuery,
  useProposalUpdateProposalMutationMutation,
} from './Proposal.generated-types'
import { useGlobalAuth } from '@headbox-ui/auth-hook'
import { ProposalHeader } from './ProposalHeader'
import { RequestSection } from './RequestSection'
import SpaceImageGallery from './SpaceImageGallery'
import { UpdateProposalInput } from '../../types'

type ProposalProps = RouteComponentProps<
{ proposalUUID: string },
StaticContext,
{ inactive: boolean }
>

const theme = createTheme({
  palette: {
    primary: {
      main: '#AF231C'
    },
    secondary: {
      main: '#2a9461'
    }
  },
  typography: {

    fontFamily: [
      'Montserrat',
      'Arial',
      'sans-serif'
    ].join(','),
    h1: {
      fontSize: 32,
      color: '#1F1F1F',
    },
    h2: {
      fontSize: 24,
      color: '#1F1F1F',
    },
    h3: {
      fontSize: 18,
      color: '#1F1F1F',
    },
    h4: {
      fontSize: 16
    },
  }
})

const RightPanel = styled(Grid)(({ theme }) => ({
  order: 2,
  [theme.breakpoints.up(960)]: {
    order: 3
  },
}))

const MainPanel = styled(Grid)(({ theme }) => ({
  order: 3,
  [theme.breakpoints.up(960)]: {
    order: 2
  }
}))

const Proposal: React.FC<ProposalProps> = ({
  history,
  match: {
    params: { proposalUUID },
  },
  location,
}) => {
  const { t } = useTranslation()
  const [canEdit, setCanEdit] = useState(false)
  const [editing, setEditing] = useState(false)
  const [proposal, setProposal] = useState(null)
  const [proposalUpdated, setProposalUpdated] = useState(false)
  const { authenticated, user, loading } = useGlobalAuth()

  const [{ fetching, error, data }] = useProposalGetProposalQueryQuery({
    variables: { UUIDs: [proposalUUID] },
  })

  const [
    { fetching: updatingProposal, error: updateError, data: updatedProposal },
    updateProposal,
  ] = useProposalUpdateProposalMutationMutation()

  useEffect(() => window.scrollTo(0, 0), [])

  useEffect(() => {
    if (!loading && authenticated && user.roles.includes('account_manager')) {
      setCanEdit(true)
    } else {
      setCanEdit(false)
    }
  }, [authenticated, user, loading])

  useEffect(() => {
    if (data?.getProposalsByUUIDs[0]) {
      setProposal(data.getProposalsByUUIDs[0])
    }
  }, [data])

  useEffect(() => {
    if (updatedProposal && isEqual(updatedProposal.updateProposal, proposal)) {
      setProposalUpdated(true)
      setEditing(false)
    } else {
      setProposalUpdated(false)
    }
  }, [proposal, updatedProposal])

  if (error || updateError || fetching || proposal == null) {
    return <LoadError load={fetching || proposal == null} error={error} />
  }

  const {
    status,
    space,
    space: { spacePhotos },
  } = proposal

  const goToConfirmation = (): void =>
    history.push({
      pathname: ROUTES.CONFIRMATION,
    })

  const handleStartEditing = (): void => {
    if (canEdit) {
      setEditing(true)
    }
  }

  const handleUpdateProposal = (): void => {
    const {
      UUID,
      baseCostDescription,
      baseCostTotal,
      currency,
      message,
      fromDateTime,
      toDateTime,
      attendees,
    } = proposal

    const proposalInput: UpdateProposalInput = {
      UUID,
      baseCostDescription,
      baseCostTotal,
      currency,
      message,
      fromDateTime,
      toDateTime,
      attendees,
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    updateProposal({ input: proposalInput })
  }

  const imageUrls = []

  if (spacePhotos && spacePhotos.length > 0) {
    spacePhotos.forEach((p) => imageUrls.push(p.url))
  }

  const isRequestedToBooked = status === 'RequestedToBook'
  const inactive = isRequestedToBooked || !authenticated

  return (
    <ThemeProvider theme={theme}>
      <div style={s.main}>
        <MetaTitle
          title={`${space?.venue?.name || ''} ${t(
            'singleProposal.metaTitle.active',
            ' Proposals - HeadBox'
          )}`}
        />
        <Grid container style={s.autoMargin}>
          <Grid item xs={12}>
            <ProposalHeader proposal={proposal}/>
          </Grid>
          <Grid item xs={12}>
            { space && <SpaceImageGallery {...space} /> }
          </Grid>
          <Grid item xs={12}>
            <FeatureBlock proposal={proposal}/>
          </Grid>
          <RightPanel item xs={12} md={4}>
            <RequestSection
              proposal={proposal}
              goToConfirmation={goToConfirmation}
              inactive={inactive}
              isRequestedToBooked={isRequestedToBooked}
              canEdit={canEdit}
              editing={editing}
              handleStartEditing={handleStartEditing}
              handleUpdateProposal={handleUpdateProposal}
              updatingProposal={updatingProposal}
              proposalUpdated={proposalUpdated}
            />
          </RightPanel>
          <MainPanel item xs={12} md={8}>
            <ProposalInfoCard proposal={proposal} editing={editing} setProposal={setProposal} />
          </MainPanel>
        </Grid>
      </div>
    </ThemeProvider>
  )
}

export default withRouter(Proposal)
